<template>
  <div class="p-6 space-y-8">
    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
      <div>
        <h2 class="text-2xl font-bold text-gray-800 mb-2">
          {{ isEditing ? 'Modifier le défi' : 'Créer un nouveau défi' }}
        </h2>
        <p class="text-gray-600">
          Configurez les conditions et récompenses pour ce défi
        </p>
      </div>
    </div>

    <form @submit.prevent ref="form" class="space-y-8">
      <!-- Informations principales -->
      <div class="bg-white rounded-xl border border-gray-200 p-6">
        <h3 class="text-lg font-semibold text-gray-800 mb-6 flex items-center">
          <i class="pi pi-info-circle mr-2 text-blue-500"></i>
          Informations générales
        </h3>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <!-- Nom du défi -->
          <div>
            <label class="mb-2 block text-sm font-medium text-gray-700">
              Nom du défi <span class="text-red-500">*</span>
            </label>
            <input
              type="text"
              v-model="milestone.name"
              required
              class="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Ex: Passer 10 commandes"
            />
          </div>

          <!-- Type de défi -->
          <div>
            <label class="mb-2 block text-sm font-medium text-gray-700">
              Type de défi <span class="text-red-500">*</span>
            </label>
            <select
              v-model="milestone.type"
              required
              class="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Sélectionner un type</option>
              <option value="order">Commande</option>
              <option value="review">Avis</option>
              <option value="referral">Parrainage</option>
              <option value="restaurant">Restaurant</option>
              <option value="spending">Dépense</option>
              <option value="social">Social</option>
              <option value="favorite">Favori</option>
              <option value="category">Catégorie</option>
              <option value="location">Localisation</option>
              <option value="custom">Personnalisé</option>
            </select>
          </div>
        </div>

        <!-- Objectif chiffré (Cible) -->
        <div class="mt-6" v-if="milestone.type">
          <label class="mb-2 block text-sm font-medium text-gray-700">
            {{ targetLabel }} <span class="text-red-500">*</span>
          </label>
          <input
            type="number"
            v-model.number="milestone.targetValue"
            min="1"
            required
            class="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            :placeholder="targetPlaceholder"
          />
        </div>

        <!-- Description -->
        <div class="mt-6">
          <label class="mb-2 block text-sm font-medium text-gray-700">
            Description
          </label>
          <textarea
            v-model="milestone.description"
            rows="3"
            class="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Décrivez ce que l'utilisateur doit faire pour compléter ce défi..."
          ></textarea>
        </div>
      </div>

      <!-- Récompenses -->
      <div class="bg-white rounded-xl border border-gray-200 p-6">
        <h3 class="text-lg font-semibold text-gray-800 mb-6 flex items-center">
          <i class="pi pi-gift mr-2 text-yellow-500"></i>
          Récompenses
        </h3>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label class="mb-2 block text-sm font-medium text-gray-700">
              XP récompense <span class="text-red-500">*</span>
            </label>
            <input
              type="number"
              v-model.number="milestone.rewardXp"
              min="0"
              required
              class="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Ex: 10"
            />
          </div>

          <div>
            <label class="mb-2 block text-sm font-medium text-gray-700">
              Points récompense <span class="text-red-500">*</span>
            </label>
            <input
              type="number"
              v-model.number="milestone.rewardPoints"
              min="0"
              required
              class="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Ex: 100"
            />
          </div>
        </div>

        <!-- Autres récompenses (Coupons, etc.) -->
        <div class="mt-6">
          <div class="flex items-center justify-between mb-2">
            <label class="block text-sm font-medium text-gray-700">
              Autres récompenses
            </label>
            <button
              type="button"
              @click="addReward"
              class="text-sm text-blue-500 hover:text-blue-600 font-medium flex items-center"
            >
              <i class="pi pi-plus mr-1"></i> Ajouter
            </button>
          </div>
          
          <div v-if="!milestone.rewards || milestone.rewards.length === 0" class="text-sm text-gray-500 italic py-2">
            Aucune récompense supplémentaire.
          </div>
          
          <div class="space-y-4" v-else>
            <div v-for="(reward, index) in milestone.rewards" :key="index" class="p-4 border border-gray-200 rounded-xl relative bg-gray-50">
              <button
                type="button"
                @click="removeReward(index)"
                class="absolute top-4 right-4 text-gray-400 hover:text-red-500 transition-colors"
                title="Supprimer"
              >
                <i class="pi pi-trash"></i>
              </button>

              <div class="mb-4 pr-8">
                <label class="block text-sm font-medium text-gray-700 mb-2">Type de récompense</label>
                <select
                  v-model="reward.type"
                  @change="handleRewardTypeChange(reward)"
                  class="w-full md:w-1/2 rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="wallet">Portefeuille (Wallet)</option>
                  <option value="coupon">Coupon de réduction</option>
                  <option value="badge">Badge (Image)</option>
                </select>
              </div>

              <!-- Wallet -->
              <div v-if="reward.type === 'wallet'" class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Montant (FCFA)</label>
                  <input
                    type="number"
                    v-model.number="reward.value"
                    class="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Label</label>
                  <input
                    type="text"
                    v-model="reward.label"
                    placeholder="Ex: Cadeau 1ère commande : 100 FCFA"
                    class="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <!-- Coupon -->
              <div v-if="reward.type === 'coupon'" class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <!-- <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">ID du coupon</label>
                  <input
                    type="text"
                    v-model="reward.value.id"
                    class="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                  />
                </div> -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Code</label>
                  <input
                    type="text"
                    v-model="reward.value.code"
                    class="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Remise</label>
                  <input
                    type="number"
                    v-model.number="reward.value.discount"
                    class="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Type de remise</label>
                  <select
                    v-model="reward.value.type"
                    class="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="percentage">Pourcentage</option>
                    <option value="fixed">Fixe</option>
                  </select>
                </div>
              </div>

              <!-- Badge -->
              <div v-if="reward.type === 'badge'">
                <label class="block text-sm font-medium text-gray-700 mb-2">Image du badge</label>
                <FileUploader
                  placeHolder="Choisir une image pour le badge"
                  :acceptedFilesTypes="['image/*']"
                  @onFileChangedV2="(data) => reward.value = data.url"
                  :fileId="reward.value"
                />
                
                <div v-if="reward.value" class="mt-3">
                  <img
                    :src="reward.value"
                    alt="Preview"
                    class="h-20 w-20 object-contain rounded-lg border"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Boutons d'action -->
      <div class="flex justify-end space-x-4">
        <button
          type="button"
          @click="$emit('cancel')"
          class="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200"
        >
          Annuler
        </button>
        <button
          type="button"
          @click="saveMilestone"
          class="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors duration-200 flex items-center space-x-2"
        >
          <i class="pi pi-save"></i>
          <span>{{ isEditing ? 'Mettre à jour' : 'Créer' }} le défi</span>
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, reactive, watch, computed, onMounted } from 'vue'
import FileUploader from '@/presentation/components/input/FileUploader.vue'

const props = defineProps({
  milestoneData: {
    type: Object,
    default: () => ({}),
  },
})

const emit = defineEmits(['saved', 'cancel'])

const form = ref(null)

const isEditing = computed(
  () => props.milestoneData && Object.keys(props.milestoneData).length > 0,
)

const milestone = reactive({
  id: '',
  name: '',
  description: '',
  type: '',
  targetValue: 1,
  rewardXp: 10,
  rewardPoints: 0,
  rewards: [],
})

const targetLabel = computed(() => {
  switch (milestone.type) {
    case 'order': return 'Nombre de commandes requis'
    case 'review': return "Nombre d'avis requis"
    case 'referral': return 'Nombre de parrainages requis'
    case 'spending': return 'Montant à dépenser (FCFA)'
    case 'social': return 'Nombre de partages'
    default: return 'Objectif (quantité/montant)'
  }
})

const targetPlaceholder = computed(() => {
  switch (milestone.type) {
    case 'order': return 'Ex: 2'
    case 'review': return "Ex: 5"
    case 'referral': return 'Ex: 3'
    case 'spending': return 'Ex: 15000'
    default: return 'Ex: 10'
  }
})

// Watcher pour les données initiales
watch(
  () => props.milestoneData,
  newData => {
    if (newData && Object.keys(newData).length > 0) {
      Object.assign(milestone, {
        id: '',
        name: '',
        description: '',
        type: '',
        targetValue: 1,
        rewardXp: 10,
        rewardPoints: 0,
        rewards: [],
        ...newData,
      })
      
      // S'assurer que rewards est un tableau
      if (!Array.isArray(milestone.rewards)) {
        milestone.rewards = []
      }
    }
  },
  { immediate: true, deep: true },
)

const addReward = () => {
  if (!milestone.rewards) milestone.rewards = []
  milestone.rewards.push({ type: 'wallet', value: 0, label: '' })
}

const handleRewardTypeChange = (reward) => {
  if (reward.type === 'wallet') {
    reward.value = 0
    reward.label = ''
  } else if (reward.type === 'coupon') {
    reward.value = {
      id: '',
      code: '',
      discount: 0,
      type: 'percentage'
    }
    delete reward.label
  } else if (reward.type === 'badge') {
    reward.value = ''
    delete reward.label
  }
}

const removeReward = (index) => {
  milestone.rewards.splice(index, 1)
}


// Méthodes
const saveMilestone = () => {
  const isValid = form.value.checkValidity()
  if (!isValid) {
    form.value.reportValidity()
    return
  }

  // Générer un ID si nécessaire
  if (!milestone.id) {
    milestone.id = `milestone_${Date.now()}`
  }

  // Nettoyer et valider les données
  const cleanMilestone = { 
    ...milestone,
    rewards: Array.isArray(milestone.rewards) 
      ? milestone.rewards.filter(r => {
          if (r.type === 'wallet') return r.value !== null && r.value !== undefined
          if (r.type === 'coupon') return r.value && r.value.code
          if (r.type === 'badge') return r.value && typeof r.value === 'string' && r.value.trim() !== ''
          return false
        }) 
      : [],
    operation: isEditing.value ? 'update' : 'add'
  }

  emit('saved', cleanMilestone)
}

// Initialisation
onMounted(() => {
  if (!props.milestoneData || Object.keys(props.milestoneData).length === 0) {
    // Définir un ID par défaut
    milestone.id = `milestone_${Date.now()}`
  }
})
</script>

<style scoped>
/* Styles spécifiques si nécessaire */
</style>
